import {
    Component,
    Output,
    EventEmitter,
    Input,
    OnInit,
    OnDestroy,
    HostListener,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CalendarService } from '@finder/shared/services/calendar-service/calendar-service.service';
import normalizeEventPartial from '@finder/shared/utils/object/normalizeEvent';
import { CommonModule } from '@angular/common';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { CalendarComponent } from '../calendar/calendar.component';

@Component({
    selector: 'finder-date-picker-wrapper',
    templateUrl: './date-picker-wrapper.component.html',
    styleUrls: ['./date-picker-wrapper.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FontIconComponent,
        CalendarComponent
    ]
})
/**
 * Date Picker Text Wrapper Component to add the calendar icon
 * and date picker to an input element or link element.
 *
 * <finder-date-picker-wrapper [(date)]="date" (dateChanged)="onDateChanged($event)">
 *     <a class="date-text">{{formattedDate}}</a>
 * </finder-date-picker-wrapper>
 *
 * You may want to avoid nested interactive controls for accessibility,
 * use dataText entry to display a span next to the calendar icon.
 *
 * <finder-date-picker-wrapper [dateText]="formattedDate" [(date)]="date" (dateChanged)="onDateChanged($event)">
 * </finder-date-picker-wrapper
 */
export class DatePickerWrapperComponent implements OnInit, OnDestroy {
    @Input() public labels: any = {};
    @Input() public hoverConfig: any = {};
    @Input() public date: Date;
    @Input() public weekDayNameLength = 1;
    @Input() public maxDays;
    @Input() public disabled;
    @Input() public dateText: string;
    @Input() highlightedDates: string[];
    @Output() public dateChanged = new EventEmitter<Date>();
    @Output() public datePickerOpen = new EventEmitter<any>();
    @Output() public datePickerClose = new EventEmitter<any>();

    opened = false;

    private destroy$ = new Subject<void>();

    constructor(public calendarService: CalendarService) {}

    /* istanbul ignore next */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event) {
        if (!event) {
            return;
        }
        // IE11 doesn't support the path object
        const partialEvent = normalizeEventPartial(event);

        // Check if the click was made inside the calendar
        const calendarPath = partialEvent.path.filter((item) => {
            let name = '';

            if (item.className?.baseVal) {
                name = item.className.baseVal;
            } else if (item.className && typeof item.className === 'string') {
                name = item.className;
            }

            return name.includes('calendar');
        });
        if (this.opened && calendarPath.length < 1) {
            // Click was not on calendar
            this.close(event);
        }
    }

    ngOnInit() {
        this.calendarService
            .getOpenStateObservable()
            .pipe(takeUntil(this.destroy$))
            .subscribe((change) => {
                this.opened = change;
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Emit the date selected to the parent
     * and close the calendar.
     *
     * @param event click event
     */
    onDateSelected(event) {
        this.dateChanged.emit(event);
        this.calendarService.closeCalendar();
        this.datePickerClose.emit(event);
    }

    onMouseEnter(event) {
        // If "Open on hover" is not enabled just return.
        if (!this.hoverConfig.enabled) {
            return;
        }

        if (this.hoverConfig.time) {
            setTimeout(() => {
                // If the hover is active after the count, open the date picker.
                if (event.target.matches(':hover')) {
                    this.open(event);
                }
            }, this.hoverConfig.time);
        } else {
            // If there isn't an intent time set, open immediately.
            this.open(event);
        }
    }

    onMouseLeave(event) {
        if (this.hoverConfig.enabled) {
            this.close(event);
        }
    }

    toggle(event) {
        this.opened ? this.close(event) : this.open(event);
    }

    open(event) {
        event.stopPropagation();
        this.calendarService.openCalendar();
        this.datePickerOpen.emit(event);
    }

    close(event) {
        event.stopPropagation();
        this.calendarService.closeCalendar();
        this.datePickerClose.emit(event);
    }
}
